'use client'

import { useCallback, useEffect, useRef } from 'react'

import {
  sendGTMEvent,
  useDecodedAccessToken,
} from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  type OrchestratedOfferingListingDetails,
  type OrchestratedAuctionListingDetails,
} from '@b-stock/fe-core/bstock-next/queries'

import { FeatureFlags } from '@/helpers/featureFlags'
import { GTM_PLATFORM } from '@/helpers/telemetry/constants'
import { orchestratedListingToGTMItemInput } from '@/helpers/telemetry/gtmItemAdapters'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import { transformListingToGTMItem } from '@/helpers/telemetry/transformListingToGTMItem'
import { watchedStatusQuery } from '@/queries/watchlistQuery'

import NotesWidget from './NotesWidget'
import {
  useHidesUnwatchedNote,
  useOutlivedListingNote,
} from './useOutlivedListingNote'

export const NotesPreviewWidget = ({
  listing,
}: {
  listing:
    | OrchestratedOfferingListingDetails
    | OrchestratedAuctionListingDetails
}) => {
  const decodedToken = useDecodedAccessToken()
  const personId = decodedToken?.personId
  const accountId = decodedToken?.accountId
  const enableListingNotes = useFlag(FeatureFlags.ENABLE_LISTING_NOTES)
  const notesAutoWatchlist = useFlag(FeatureFlags.NOTES_AUTO_WATCHLIST)
  const { trackButtonClicked } = useAnalytics()
  const ctaViewedFiredRef = useRef(false)

  const { data, isError: isWatchedStateError } = watchedStatusQuery.useWithOpts(
    [{ personId, listingId: listing._id }],
    { enabled: !!personId && enableListingNotes }
  )
  const outlivedNote = useOutlivedListingNote(personId, listing._id)
  const hidesStoredNote = useHidesUnwatchedNote(data?.isWatchlisted)

  useEffect(() => {
    if (!notesAutoWatchlist) return
    if (!data) return
    if (ctaViewedFiredRef.current) return
    ctaViewedFiredRef.current = true
    trackButtonClicked({
      buttonName: 'listing_note_cta_viewed',
      listing_id: listing._id,
      listing_watchlist_status: data.isWatchlisted,
      character_count: data.note?.length ?? 0,
      note_value: data.note ?? '',
    })
  }, [data, notesAutoWatchlist, listing._id, trackButtonClicked])

  const onAutoWatchlistAdd = useCallback(() => {
    const wishlistItem = transformListingToGTMItem(
      orchestratedListingToGTMItemInput(listing),
      1
    )
    sendGTMEvent({ ecommerce: null })
    sendGTMEvent({
      event: 'add_to_wishlist',
      platform: GTM_PLATFORM,
      ecommerce: {
        currency: 'USD',
        value: wishlistItem.price || undefined,
        items: [wishlistItem],
      },
    })
    trackButtonClicked({
      buttonName: 'add_to_watchlist',
      listing_id: listing._id,
    })
    trackButtonClicked({
      buttonName: 'toggle_listing_watched',
      button_source: 'note_saved',
      listing_id: listing._id,
    })
  }, [listing, trackButtonClicked])

  if (!enableListingNotes) return null
  if (!personId || !accountId) return null

  const listingEndTime =
    'auction' in listing && listing.auction
      ? (listing.auction.actualEndTime ?? listing.auction.initialEndTime)
      : undefined

  if (notesAutoWatchlist) {
    if (!data) return null
    return (
      <NotesWidget
        listingId={listing._id}
        personId={personId}
        accountId={accountId}
        dateExpires={listing.dateVisibilityEnds}
        note={data.note}
        defaultMode="view"
        wasWatchlisted={data.isWatchlisted}
        listingEndTime={listingEndTime}
        onAutoWatchlistAdd={onAutoWatchlistAdd}
        hidesStoredNote={hidesStoredNote}
      />
    )
  }

  if (!data?.isWatchlisted && !outlivedNote) return null

  return (
    <NotesWidget
      listingId={listing._id}
      personId={personId}
      accountId={accountId}
      dateExpires={listing.dateVisibilityEnds}
      note={data?.note ?? outlivedNote}
      defaultMode="view"
      wasWatchlisted={data?.isWatchlisted}
      isWatchedStateError={isWatchedStateError}
      listingEndTime={listingEndTime}
    />
  )
}
